import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Helmet } from "react-helmet-async";

import {
  Container,
  Button,
  Alert,
  TextField,
  Typography,
  Paper,
  Box,
  FormControl,
  Select,
  Divider,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useTranslation } from "react-i18next";

import { updateUserGoal, getUserPhoneNumber } from "../../api/users";
import {
  startVerification,
  checkVerificationCode,
} from "../../api/phoneVerification";

const Welcome = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const goalSchema = yup.object().shape({
    goal: yup.string().required(t("errors.account_goal_required")),
  });

  const phoneSchema = yup.object({
    phoneNumber: yup
      .string()
      .transform((value) => {
        if (!value) return value; // keep empty string for required check
        let v = value.replace(/\s+/g, ""); // remove spaces
        if (!v.startsWith("+")) {
          v = "+371" + v; // prepend +371 if no code
        }
        return v;
      })
      .matches(/^\+[0-9]{8,15}$/, t("errors.phone_invalid")) // validate full number
      .required(t("errors.phone_required")),
  });

  const codeSchema = yup.object({
    verificationCode: yup
      .string()
      .required(t("errors.verification_code_required")),
  });

  const [goalSelection, setGoalSelection] = useState(null);
  const [welcomeStep, setWelcomeStep] = useState(0);
  const [showIncorrectCodeError, setShowIncorrectCodeError] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("00 0000 000");

  // Forms initialization
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(goalSchema),
  });

  const {
    register: registerCode,
    handleSubmit: handleCodeSubmit,
    formState: { errors: codeErrors },
  } = useForm({
    resolver: yupResolver(codeSchema),
  });

  const {
    register: registerPhone,
    handleSubmit: handlePhoneSubmit,
    formState: { errors: phoneErrors },
    setValue,
    control: controlPhone,
  } = useForm({
    resolver: yupResolver(phoneSchema),
  });

  // Handle goal form submit
  const handleSubmitGoal = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const selectedGoal = formData.get("goal");
    setGoalSelection(selectedGoal);
    await updateUserGoal(selectedGoal);

    if (selectedGoal === "both" || selectedGoal === "sell") {
      setWelcomeStep(3);
    } else if (selectedGoal === "buy") {
      navigate("/visas_izsoles");
    }
  };

  // Handle phone number verification start
  const handleStartVerification = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newPhoneNumber = formData.get("phoneNumber");

    if (newPhoneNumber !== phoneNumber) {
      // TODO update the database with the new phone number
      setPhoneNumber(newPhoneNumber);
    }

    const didStartVerification = await startVerification(newPhoneNumber);
    const success = didStartVerification.success || null;
    if (didStartVerification && success == true) {
      setWelcomeStep(1);
    } else {
      alert(t("errors.err"));
    }
  };

  // Handle verification code submission
  const handleCheckVerification = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const verificationCode = formData.get("verificationCode");

    const isVerified = await checkVerificationCode(
      phoneNumber,
      verificationCode
    );
    if (isVerified) {
      setWelcomeStep(2);
    } else {
      setShowIncorrectCodeError(true);
    }
  };

  // Fetch phone number from API on mount
  const fetchPhoneNumber = async () => {
    try {
      const userPhoneNumber = await getUserPhoneNumber();

      if (!userPhoneNumber || !userPhoneNumber.phone) {
        alert(t("welcome_page.phone_fetch_error"));
      } else {
        setPhoneNumber(userPhoneNumber.phone);
        setValue("phoneNumber", userPhoneNumber.phone);
      }
    } catch (err) {
      alert(t("welcome_page.phone_fetch_error"));
    }
  };

  useEffect(() => {
    fetchPhoneNumber();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t("seo.title.welcome")}</title>
        <meta property="og:title" content={t("seo.title.welcome")} />

        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {welcomeStep === 0 && (
          <Box sx={{ padding: 3 }}>
            <Paper sx={{ padding: 4 }}>
              <Typography variant="h4">
                {t("verification_step.title")}
              </Typography>
              <Typography gutterBottom>
                {t("verification_step.subtitle")}
              </Typography>
              <Divider sx={{ pt: 2, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {t("verification_step.title2")}
              </Typography>
              <Typography gutterBottom>
                {t("verification_step.subtitle2")}
              </Typography>
              <form onSubmit={handleStartVerification}>
                <Controller
                  name="phoneNumber"
                  control={controlPhone}
                  defaultValue={phoneNumber}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("forms.phone")}
                      variant="outlined"
                      margin="normal"
                      size="small"
                      error={!!phoneErrors.phoneNumber}
                      helperText={phoneErrors.phoneNumber?.message}
                      type="tel"
                    />
                  )}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  {t("verification_step.start")}
                </Button>
              </form>
            </Paper>
          </Box>
        )}

        {welcomeStep === 1 && (
          <Box sx={{ padding: 3 }}>
            <Paper sx={{ padding: 4 }}>
              <Typography variant="h4">
                {t("verification_step.title3")}
              </Typography>
              <Typography gutterBottom>
                {t("verification_step.subtitle3")}
              </Typography>
              <form onSubmit={handleCheckVerification}>
                <TextField
                  label={t("forms.verification_code")}
                  variant="outlined"
                  margin="normal"
                  size="small"
                  {...registerCode("verificationCode")}
                  error={!!codeErrors.verificationCode}
                  helperText={codeErrors.verificationCode?.message}
                  type="text"
                />
                {showIncorrectCodeError && (
                  // TODO fix visual style
                  <Alert severity="error">
                    {t("errors.verification_code_incorrect")}
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  {t("verification_step.finish")}
                </Button>
              </form>
            </Paper>
          </Box>
        )}

        {welcomeStep === 2 && (
          <Box sx={{ padding: 3 }}>
            <Paper sx={{ padding: 4 }}>
              {/* <Typography variant="h4">{t("welcome_page.title")}</Typography>
              <Typography gutterBottom>{t("welcome_page.subtitle")}</Typography>
              <Divider sx={{ pt: 2, mb: 2 }} /> */}
              <Typography variant="h5" gutterBottom>
                {t("welcome_page.last_step_title")}
              </Typography>
              <Typography gutterBottom>
                {t("welcome_page.last_step_copy")}
              </Typography>
              <form onSubmit={handleSubmitGoal}>
                <FormControl
                  size="small"
                  fullWidth
                  margin="normal"
                  sx={{ maxWidth: "20%" }}
                >
                  <Controller
                    name="goal"
                    control={control}
                    defaultValue="sell"
                    render={({ field }) => (
                      <Select
                        {...field}
                        // error={!!errors.goal}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="sell">
                          {t("welcome_page.goal_sell")}
                        </MenuItem>
                        <MenuItem value="buy">
                          {t("welcome_page.goal_buy")}
                        </MenuItem>
                        <MenuItem value="both">
                          {t("welcome_page.goal_both")}
                        </MenuItem>
                      </Select>
                    )}
                  />
                  <Typography variant="body2" color="error">
                    {errors.goal?.message}
                  </Typography>
                </FormControl>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  {t("welcome_page.cta")}
                </Button>
              </form>
            </Paper>
          </Box>
        )}

        {welcomeStep === 3 && (
          <Box sx={{ padding: 3 }}>
            <Paper sx={{ padding: 4 }}>
              <Typography variant="h4">
                {t("welcome_page.step2_title")}
              </Typography>
              <Typography gutterBottom>
                {t("welcome_page.step2_subtitle")}
              </Typography>
              {/* <Divider sx={{ pt: 2, mb: 2 }}></Divider> */}
              {/* <Typography variant="h5" gutterBottom>
                {t("welcome_page.step2_last_step_title")}
              </Typography> */}
              {/* <Typography gutterBottom>
                {t("welcome_page.step2_last_step_copy")}
              </Typography> */}
              <Button
                component={Link}
                to="/profils?tab=payment"
                variant="contained"
                color="secondary"
                sx={{ mt: 2, display: "inline-block" }}
              >
                {t("welcome_page.step2_cta")}
              </Button>
              <Button
                component={Link}
                to="/visas_izsoles"
                variant="outlined"
                color="primary"
                sx={{ ml: 1, mt: 2, display: "inline-block" }}
              >
                {t("welcome_page.step2_skip")}
              </Button>
            </Paper>
          </Box>
        )}
      </Container>
    </>
  );
};

export default Welcome;
